import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import tw from "twin.macro";
import Modal from "react-modal";
import GradientButton from "../../components/GradientButton";

const COLORS = {
  teal: "#4f8884",
  gold: "#c6ab57",
  orange: "#A4400A",
};

const DifferencesModalRoot = styled(Modal)`
  position: absolute;
  inset: 50% auto auto 50%;
  transform: translate(-50%, -50%);
  width: min(1180px, calc(100vw - 32px));
  max-height: calc(100vh - 32px);
  overflow: auto;
  background: #fff;
  border-radius: 8px;
  outline: none;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.24);
`;

const ModalInner = styled.div`
  ${tw`relative`}
  padding: 2.8rem 2rem 2.5rem;
  background: #fff;

  @media (min-width: 1024px) {
    padding: 3.2rem 4rem 3rem;
  }
`;

const ModalClose = styled.button`
  ${tw`absolute`}
  top: 1rem;
  right: 1rem;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.06);
  color: #444;
  font-size: 20px;
  cursor: pointer;
  z-index: 3;
`;

const IntroRow = styled.div`
  ${tw`grid gap-6 items-center`}

  @media (min-width: 1024px) {
    grid-template-columns: 120px 1fr;
    column-gap: 1rem;
  }
`;

const BrandLogo = styled.img`
  width: 106px;
  height: auto;
  display: block;
`;

const IntroText = styled.p`
  margin: 0;
  font-family: "Montserrat", sans-serif;
  font-size: 18px;
  line-height: 1.2;
  font-weight: 700;
  color: #4d4b4b;
  text-transform: uppercase;
  margin-left: 8px;

  @media (min-width: 1024px) {
    font-size: 20px;
  }
`;

const Divider = styled.div`
  margin: 2rem 0 1.8rem;
  border-top: 2px solid #d6d6d6;
`;

const Message = styled.p`
  margin: 0 auto;
  max-width: 920px;
  font-family: "Montserrat", sans-serif;
  font-size: 18px;
  line-height: 1.16;
  font-weight: 400;
  color: #4d4b4b;
  text-transform: uppercase;
  text-align: left;

  strong {
    color: ${COLORS.orange};
    font-weight: 700;
  }

  @media (min-width: 768px) {
    text-align: left;
  }
`;

const Grid = styled.div`
  ${tw`flex flex-col`}
  margin-top: 3rem;
  gap: 2.8rem;

  @media (max-width: 1023px) {
    display: none;
  }
`;

const GridRow = styled.div`
  ${tw`grid gap-x-6 gap-y-10 justify-center`}
  grid-template-columns: repeat(2, minmax(120px, 1fr));

  @media (min-width: 768px) and (max-width: 1023px) {
    grid-template-columns: repeat(3, minmax(140px, 1fr));
  }

  @media (min-width: 1024px) {
    display: flex;
    justify-content: center;
    gap: 1.25rem;
  }
`;

const ResponsiveGrid = styled.div`
  ${tw`grid gap-x-6 gap-y-10 justify-center`}
  margin-top: 3rem;
  grid-template-columns: repeat(2, minmax(120px, 1fr));

  @media (min-width: 768px) and (max-width: 1023px) {
    grid-template-columns: repeat(3, minmax(140px, 1fr));
  }

  @media (min-width: 1024px) {
    display: none;
  }
`;

const Item = styled.div`
  text-align: center;

  @media (min-width: 1024px) {
    width: 150px;
  }
`;

const ItemIcon = styled.img`
  width: 54px;
  height: 54px;
  object-fit: contain;
  display: block;
  margin: 0 auto;

  @media (min-width: 768px) {
    width: 80px;
    height: 80px;
  }

  @media (min-width: 1024px) {
    width: 64px;
    height: 64px;
  }
`;

const ItemLabel = styled.div`
  margin-top: 1rem;
  min-height: 44px;
  white-space: pre-line;
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  line-height: 1.15;
  font-weight: 600;
  color: #437672;
  text-transform: uppercase;

  @media (min-width: 768px) {
    min-height: 50px;
    font-size: 15px;
  }

  @media (min-width: 1024px) {
    min-height: 54px;
    font-size: 16px;
  }
`;

const BottomBar = styled.div`
  ${tw`flex justify-center`}
  margin-top: 2.2rem;
`;

const PrimaryButton = styled(GradientButton)`
  min-width: 280px;
  padding: 12px 30px;
  font-family: "Montserrat", sans-serif;
  font-size: 16px;
  line-height: 1.1;
  font-weight: 500;
`;

export default function DifferencesModal({ isOpen, onRequestClose, items }) {
  const desktopRows = [5, 5, 5, 4, 4];
  const rows = [];
  let startIndex = 0;

  desktopRows.forEach((size) => {
    rows.push(items.slice(startIndex, startIndex + size));
    startIndex += size;
  });

  return (
    <DifferencesModalRoot
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      shouldCloseOnOverlayClick
      overlayClassName="plans-modal-overlay"
    >
      <ModalInner>
        <ModalClose onClick={onRequestClose} aria-label="Fechar modal">
          ×
        </ModalClose>

        <IntroRow>
          <BrandLogo src="/novas-imagens/logo.png" alt="Trataz" />
          <IntroText>
            OS PROFISSIONAIS POSSUEM AUTONOMIA PARA CRIAR SEUS
            <br />
            PROCESSOS ATRAVÉS DE VÍDEOS, IMAGENS E TEXTOS, DE
            <br />
            ACORDO COM A NECESSIDADE DA CADA PACIENTE.
          </IntroText>
        </IntroRow>

        <Divider />

        <Message>
          <strong>OS DIFERENCIAIS</strong> FAZEM PARTE DE UMA EXECUÇÃO{" "}
          <strong>LÓGICA</strong>
          <br />
          <strong>E EFICIENTE</strong> ONDE O PACIENTE RECEBE CONSTANTE
          <br />
          <strong>DIRECIONAMENTO</strong> E ACOMPANHAMENTO{" "}
          <strong>NOS EXERCÍCIOS.</strong>
        </Message>

        <ResponsiveGrid>
          {items.map((item) => (
            <Item key={`responsive-${item.src}`}>
              <ItemIcon src={item.src} alt={item.label.replace(/\n/g, " ")} />
              <ItemLabel>{item.label}</ItemLabel>
            </Item>
          ))}
        </ResponsiveGrid>

        <Grid>
          {rows.map((row, rowIndex) => (
            <GridRow key={`row-${rowIndex}`}>
              {row.map((item) => (
                <Item key={item.src}>
                  <ItemIcon
                    src={item.src}
                    alt={item.label.replace(/\n/g, " ")}
                  />
                  <ItemLabel>{item.label}</ItemLabel>
                </Item>
              ))}
            </GridRow>
          ))}
        </Grid>

        <BottomBar>
          <PrimaryButton
            as={Link}
            to="https://www.trataz.com.br/login"
            target="_blank"
            rel="noopener noreferrer"
          >
            COMEÇAR GRATUITAMENTE
          </PrimaryButton>
        </BottomBar>
      </ModalInner>
    </DifferencesModalRoot>
  );
}
